import React, { useEffect, useState, useCallback } from 'react';
import {
  UserPlus, Trash2, Pencil, RefreshCw, X, Loader2, ShieldCheck, Crown, FileEdit, Inbox, Eye,
} from 'lucide-react';
import { api, formatApiError } from '../../lib/api';
import { PageHeader, AdminCard, Loading, Empty, Btn, Input, Select } from './shared';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../context/AuthContext';

/**
 * AdminUsers — owner-only panel user management.
 *
 * Lists every admin-panel account with its role, lets the owner create,
 * edit (name / role / password reset) and delete users. Backend guards
 * the last-owner / self-delete edge cases; the UI mirrors them.
 */
const ROLE_META = {
  owner:   { Icon: Crown,       tone: 'amber',   label: 'Owner',   desc: 'Full access incl. users & settings' },
  editor:  { Icon: FileEdit,    tone: 'violet',  label: 'Editor',  desc: 'Content: pages, blog, media, SEO' },
  support: { Icon: Inbox,       tone: 'sky',     label: 'Support', desc: 'Inbox: leads, partners, orders' },
  viewer:  { Icon: Eye,         tone: 'slate',   label: 'Viewer',  desc: 'Read-only across the panel' },
};
const TONE = {
  amber:  'bg-amber-50 text-amber-700 border-amber-200',
  violet: 'bg-violet-50 text-violet-700 border-violet-200',
  sky:    'bg-sky-50 text-sky-700 border-sky-200',
  slate:  'bg-slate-100 text-slate-600 border-slate-200',
};

const BLANK = { email: '', name: '', password: '', role: 'viewer' };

export default function AdminUsers() {
  const { toast } = useToast();
  const { user: me } = useAuth();
  const [items, setItems] = useState([]);
  const [roles, setRoles] = useState(['owner', 'editor', 'support', 'viewer']);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);  // existing user or BLANK
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/admin/users');
      setItems(res.data?.items || []);
      if (res.data?.roles) setRoles(res.data.roles);
    } catch (err) {
      toast({ title: 'Failed to load users', description: formatApiError(err) });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      if (editing.id) {
        const body = { name: editing.name, role: editing.role };
        if (editing.password) body.password = editing.password;
        await api.put(`/api/admin/users/${editing.id}`, body);
      } else {
        if (!editing.email || !editing.password) {
          toast({ title: 'Email and password are required' });
          setSaving(false);
          return;
        }
        await api.post('/api/admin/users', editing);
      }
      toast({ title: 'Saved' });
      setEditing(null);
      load();
    } catch (err) {
      toast({ title: 'Save failed', description: formatApiError(err) });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.email}? This can't be undone.`)) return;
    try {
      await api.delete(`/api/admin/users/${u.id}`);
      setItems((arr) => arr.filter((x) => x.id !== u.id));
      toast({ title: 'User deleted' });
    } catch (err) {
      toast({ title: 'Delete failed', description: formatApiError(err) });
    }
  };

  return (
    <>
      <PageHeader
        title={<span className="flex items-center gap-2"><ShieldCheck size={18} /> Team & access</span>}
        subtitle="Manage who can sign in to the admin panel and what they can do"
        action={
          <div className="flex gap-2">
            <Btn variant="ghost" onClick={load}><RefreshCw size={14} /> Refresh</Btn>
            <Btn onClick={() => setEditing({ ...BLANK })}><UserPlus size={14} /> Add user</Btn>
          </div>
        }
      />

      {/* Role legend */}
      <AdminCard title="Roles" className="mb-4">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {Object.entries(ROLE_META).map(([key, m]) => (
            <div key={key} className={`rounded-xl border p-3 ${TONE[m.tone]}`}>
              <p className="font-bold flex items-center gap-1.5 text-sm"><m.Icon size={13} /> {m.label}</p>
              <p className="text-[11px] mt-1 opacity-80">{m.desc}</p>
            </div>
          ))}
        </div>
      </AdminCard>

      <AdminCard>
        {loading ? <Loading /> : items.length === 0 ? (
          <Empty>No users yet.</Empty>
        ) : (
          <div className="divide-y divide-white/5">
            {items.map((u) => {
              const m = ROLE_META[u.role] || ROLE_META.viewer;
              const isMe = me?.id === u.id || me?.email === u.email;
              return (
                <div key={u.id} className="py-3 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white flex items-center justify-center font-bold flex-shrink-0">
                    {(u.name || u.email || '?').charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-white truncate flex items-center gap-2">
                      {u.name || u.email}
                      {isMe && <span className="text-[10px] font-bold uppercase tracking-wider bg-white/10 text-slate-300 px-1.5 py-0.5 rounded">you</span>}
                      {u.twofa_enabled && <ShieldCheck size={12} className="text-emerald-400" title="2FA enabled" />}
                    </p>
                    <p className="text-xs text-slate-400 truncate">{u.email}</p>
                  </div>
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md border text-[11px] font-bold ${TONE[m.tone]}`}>
                    <m.Icon size={11} /> {m.label}
                  </span>
                  <div className="flex gap-1">
                    <Btn size="sm" variant="ghost" onClick={() => setEditing({ ...u, password: '' })}><Pencil size={12} /></Btn>
                    {!isMe && <Btn size="sm" variant="danger" onClick={() => remove(u)}><Trash2 size={12} /></Btn>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </AdminCard>

      {/* Edit / create drawer */}
      {editing && (
        <div className="fixed inset-0 z-50 flex" onClick={() => !saving && setEditing(null)}>
          <div className="flex-1 bg-black/60 backdrop-blur-sm" />
          <aside className="w-full max-w-md bg-slate-900 border-l border-white/10 p-6 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-white">{editing.id ? 'Edit user' : 'New user'}</h3>
              <button onClick={() => setEditing(null)} className="p-2 rounded-lg hover:bg-white/5"><X size={18} /></button>
            </div>

            <div className="space-y-3">
              <Input
                label="Email"
                value={editing.email || ''}
                onChange={(e) => setEditing({ ...editing, email: e.target.value })}
                disabled={!!editing.id}
                placeholder="person@clisecure.com"
              />
              <Input
                label="Name"
                value={editing.name || ''}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                placeholder="Full name"
              />
              <Select
                label="Role"
                value={editing.role}
                onChange={(e) => setEditing({ ...editing, role: e.target.value })}
              >
                {roles.map((r) => <option key={r} value={r}>{ROLE_META[r]?.label || r}</option>)}
              </Select>
              <Input
                label={editing.id ? 'Reset password (leave blank to keep)' : 'Password'}
                type="password"
                value={editing.password || ''}
                onChange={(e) => setEditing({ ...editing, password: e.target.value })}
                placeholder={editing.id ? '••••••••' : 'Strong password'}
                autoComplete="new-password"
              />
            </div>

            <div className="mt-6 flex gap-2 pt-4 border-t border-white/10">
              <Btn onClick={save} disabled={saving}>{saving ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : 'Save'}</Btn>
              <Btn variant="ghost" onClick={() => setEditing(null)} disabled={saving}>Cancel</Btn>
            </div>
          </aside>
        </div>
      )}
    </>
  );
}
